import { CButtonClassname, EButtonColor } from '@web_opxp/components';
import {
  AuthSelectors,
  ConfigSelectors,
  ELanguage,
  FirebaseActions,
  FirebaseSelectors,
  LanguageSelectors,
  RemoteConfigService,
  renderStringToReact,
  useTranslation,
} from '@web_opxp/features';
import { FC, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { DEFAULT_WELCOME_LP, DEFAULT_WELCOME_MESSAGE } from '../base/constants';
import ENV from '../base/env';
import { getUserPageUrl } from '../base/historyService';
import LevelSection from '../components/LevelSection/LevelSection';
import { CyclesSelectors } from '../cycles';
import { ExerciseBaseRoute } from '../exercise/Exercise';
import { ExercisesRoute } from '../exercises';
import { SessionSelectors } from '../session';
import { IState } from '../store/state';

export interface ISessionSectionProps {
  cycleLength: number;
  exerciseLink: string;
  experimentVariant: string | number;
  isPersonlisedWelcomeMessage: boolean;
  isSessionContinue: boolean;
  shouldLinkToWelcomeTour: boolean;
  language?: string;
  sessionId: number;
  token?: string;
  usernameValue?: string;
  onPersonalizedWelcomeMessage: (experiment: string, variant: string | number) => void;
}

export const SessionSectionFC: FC<ISessionSectionProps> = ({
  cycleLength,
  exerciseLink,
  experimentVariant,
  isPersonlisedWelcomeMessage,
  isSessionContinue,
  shouldLinkToWelcomeTour,
  language,
  sessionId,
  token,
  usernameValue,
  onPersonalizedWelcomeMessage,
}) => {
  const { t } = useTranslation();
  const [hasSeenWelcomeLp, setHasSeenWelcomeLp] = useState(false);

  const sessionsLeft = sessionId === 0 ? cycleLength : cycleLength - sessionId + 1;
  const userPageLink = getUserPageUrl(language, token);
  // Don't change the following to ?? because usernameValue can be empty string
  const username = usernameValue || `<a href="${userPageLink}">${t('Dashboard.Greeting.SetUsername')}</a>`;

  const [titleId, setTitleId] = useState('Dashboard.Title');

  const welcomeTourLink = `/welcome_tour`;

  const linkTo =
    shouldLinkToWelcomeTour && !hasSeenWelcomeLp ? welcomeTourLink : `${ExerciseBaseRoute}/${exerciseLink}`;

  const trackExperiment = useCallback(() => {
    onPersonalizedWelcomeMessage('experiment_4497', experimentVariant);
  }, [experimentVariant, onPersonalizedWelcomeMessage]);

  // Check local storage if user has seen the welcome lp experiment
  useEffect(() => {
    const localStorageWelcomeLp = !!localStorage.getItem('welcome_lp');
    setHasSeenWelcomeLp(localStorageWelcomeLp);
  }, []);

  useEffect(() => {
    // track experiment on firebase
    trackExperiment();
    // if personalized message turned off - do nothing
    if (!isPersonlisedWelcomeMessage) {
      return;
    }
    // calculate proper translation key depending on daytime
    const currentHour = new Date().getHours();
    let titleTranslationKey = 'Dashboard.Greeting.Morning';
    if (currentHour >= 11 && currentHour < 18) {
      titleTranslationKey = 'Dashboard.Greeting.Day';
    } else if (currentHour < 4 || currentHour >= 18) {
      titleTranslationKey = 'Dashboard.Greeting.Evening';
    }
    setTitleId(titleTranslationKey);
  }, [isPersonlisedWelcomeMessage, trackExperiment]);

  const titleContent = t(titleId, { username, sessionId });
  const title = renderStringToReact(titleContent);

  return (
    <LevelSection
      isPersonlisedWelcomeMessage={isPersonlisedWelcomeMessage}
      title={title}
      subtitle={t('Dashboard.Intro', { cycleLength, sessionsLeft })}>
      <Link className={`${CButtonClassname} ${EButtonColor.white} session-section-link`} to={linkTo}>
        {isSessionContinue ? t('Dashboard.CTA.Continue') : t('Dashboard.CTA.Start')}
      </Link>
      <Link className={`${CButtonClassname} ${EButtonColor.white} session-section-link`} to={ExercisesRoute}>
        {t('Dashboard.CTA.Select')}
      </Link>
    </LevelSection>
  );
};

export const mapStateToProps = (state: IState) => {
  const cycleLength = CyclesSelectors.getCycleLength(state);
  const cycleId = CyclesSelectors.getCurrentCycleId(state);
  const workoutId = SessionSelectors.getActiveWorkoutId(state);
  const exerciseId = ConfigSelectors.getWorkoutExerciseId(state, workoutId);
  const exerciseLink = exerciseId.toLowerCase();
  const isCurrentSessionCompleted = SessionSelectors.allWorkoutsCompleted(state);
  const isSessionStart = SessionSelectors.isSessionStart(state);
  const isSessionContinue = !isSessionStart && !isCurrentSessionCompleted;
  const sessionId = CyclesSelectors.getCurrentSessionId(state);
  const token = AuthSelectors.getToken(state);
  const language = LanguageSelectors.getLanguage(state);
  const isFirebaseReady = FirebaseSelectors.isReady(state);
  const welcomeMessageConfig = isFirebaseReady
    ? RemoteConfigService.getObjectConfigValue(ENV.RemoteConfigWelcomeMessage, DEFAULT_WELCOME_MESSAGE)
    : undefined;
  const isPersonlisedWelcomeMessage = !!welcomeMessageConfig?.welcome_message;
  const welcomeLpConfig = isFirebaseReady
    ? RemoteConfigService.getObjectConfigValue(ENV.RemoteConfigWelcomeLp, DEFAULT_WELCOME_LP)
    : undefined;
  const isWelcomeLpEnabled = welcomeLpConfig?.enabled;
  // Don't change the following to ?? because variant can be empty string
  const experimentVariant = welcomeMessageConfig?.variant || DEFAULT_WELCOME_MESSAGE.variant;
  const usernameValue = AuthSelectors.getUserName(state);

  const shouldLinkToWelcomeTour =
    cycleId === 1 && sessionId === 1 && !!isWelcomeLpEnabled && language === ELanguage.German;

  return {
    cycleLength,
    exerciseLink,
    experimentVariant,
    isPersonlisedWelcomeMessage,
    isSessionContinue,
    shouldLinkToWelcomeTour,
    language,
    sessionId,
    token,
    usernameValue,
  };
};

export const mapDispatchToProps = {
  onPersonalizedWelcomeMessage: FirebaseActions.setUserPropertyAction,
};

const SessionSection = connect(mapStateToProps, mapDispatchToProps)(SessionSectionFC);
SessionSection.displayName = 'SessionSection';

export default SessionSection;
